/* eslint-disable complexity */
import { obsMetricNames, type ObsMetricAlias } from "@/resources/observations_metricUtils";
import { O2_SATURATION_LIMITS } from "@/components/ObservationChart";

export enum AlertType {
  /** No colour - Healthy range */
  DEFAULT = 0,
  /** Yellow - Low severity */
  YELLOW = 1,
  /** Orange - Medium Low severity */
  ORANGE = 2,
  /** Red - Medium severity */
  WARNING = 3,
  /** Purple - Highest severity */
  DANGER = 4,
}

type ValidationContext = {
  hasHypercapnicRespiratoryFailure?: boolean;
  currentO2Flow?: number | null;
};

/**
 * Converts a NEWS score (1-4) to the corresponding AlertType.
 * NEWS scores are used in the observation chart to indicate severity,
 * while AlertType is used for validation alerts.
 */
const news2AlertType = (news: 1 | 2 | 3 | 4 | undefined): AlertType => {
  switch (news) {
    case 1: return AlertType.YELLOW;
    case 2: return AlertType.ORANGE;
    case 3: return AlertType.WARNING;
    case 4: return AlertType.DANGER;
    default: return AlertType.DEFAULT;
  }
};

// Define the specific field checks first
const specificFieldChecks = {
  [obsMetricNames.respirations]: (value: number | string | undefined): AlertType => {
    if (value !== undefined && value !== "") {
      const numValue = Number(value);
      if (Number.isNaN(numValue)) return AlertType.DEFAULT;

      if (numValue >= 36 || numValue <= 4) return AlertType.DANGER;
      if ((numValue >= 30 && numValue <= 35) || (numValue >= 5 && numValue <= 9)) return AlertType.WARNING;
      if (numValue >= 25 && numValue <= 29) return AlertType.ORANGE;
      if (numValue >= 21 && numValue <= 24) return AlertType.YELLOW;
    }
    return AlertType.DEFAULT;
  },
  [obsMetricNames.o2Sats]: (value: number | string | undefined, context?: ValidationContext): AlertType => {
    if (value !== undefined && value !== "") {
      const numValue = Number(value);
      if (Number.isNaN(numValue)) return AlertType.DEFAULT;

      const hasHypercapnicRespiratoryFailure = context?.hasHypercapnicRespiratoryFailure;
      const currO2FlowRateIsZero = context?.currentO2Flow === 0 || context?.currentO2Flow == null || !context?.currentO2Flow;

      // Get the appropriate limits based on context
      let limits: typeof O2_SATURATION_LIMITS[keyof typeof O2_SATURATION_LIMITS];
      if (!hasHypercapnicRespiratoryFailure) {
        limits = O2_SATURATION_LIMITS.default;
      } else if (currO2FlowRateIsZero) {
        limits = O2_SATURATION_LIMITS.hypercapnicZeroFlow;
      } else {
        limits = O2_SATURATION_LIMITS.hypercapnicWithFlow;
      }

      // Find the matching range and return its news value (or DEFAULT if none match)
      for (const range of limits.ranges) {
        if ("range" in range && range.range.min <= numValue && numValue <= range.range.max) {
          return news2AlertType(range.news);
        }
      }
    }
    return AlertType.DEFAULT;
  },
  [obsMetricNames.o2Flow]: (value: number | string | undefined): AlertType => {
    if (value !== undefined) {
      const numValue = Number(value);
      if (Number.isNaN(numValue)) return AlertType.DEFAULT;

      if (numValue >= 10) return AlertType.WARNING;
      if (numValue >= 7) return AlertType.ORANGE;
    }
    return AlertType.DEFAULT;
  },
  [obsMetricNames.heartRate]: (value: number | string | undefined): AlertType => {
    if (value !== undefined && value !== "") {
      const numValue = Number(value);
      if (Number.isNaN(numValue)) return AlertType.DEFAULT;

      if (numValue >= 140 || numValue <= 30) return AlertType.DANGER;
      if ((numValue >= 31 && numValue < 50) || (numValue >= 120 && numValue < 140)) return AlertType.WARNING;
      if (numValue >= 110) return AlertType.ORANGE;
      if ((numValue >= 50 && numValue < 60) || (numValue >= 100 && numValue < 110)) return AlertType.YELLOW;
    }
    return AlertType.DEFAULT;
  },
  [obsMetricNames.bloodPresSystolic]: (value: number | string | undefined): AlertType => {
    if (value !== undefined && value !== "") {
      const numValue = Number(value);
      if (Number.isNaN(numValue)) return AlertType.DEFAULT;

      if (numValue >= 200) return AlertType.WARNING;
      if (numValue >= 170) return AlertType.ORANGE;
      if (numValue >= 160) return AlertType.YELLOW;
      if (numValue < 90) return AlertType.DANGER;
      if (numValue < 100) return AlertType.WARNING;
      if (numValue < 110) return AlertType.ORANGE;
    }
    return AlertType.DEFAULT;
  },
  // TODO why don't we check for diastolic?
  // bloodPresDiastolic: (value) => {
  //   if (value !== undefined && value !== "") {
  //     const numValue = Number(value);
  //     if (Number.isNaN(numValue)) return AlertType.DEFAULT;
  //
  //     // upper band
  //     if (numValue >= 120) return AlertType.WARNING;
  //     if (numValue >= 100) return AlertType.ORANGE;
  //     if (numValue >= 90) return AlertType.YELLOW;
  //
  //     // lower band
  //     if (numValue < 60) return AlertType.DANGER;
  //     if (numValue < 70) return AlertType.WARNING;
  //     if (numValue < 80) return AlertType.ORANGE;
  //   }
  //   return AlertType.DEFAULT;
  // },
  [obsMetricNames.temperature]: (value: number | string | undefined): AlertType => {
    if (value !== undefined && value !== "") {
      const numValue = Number(value);
      if (Number.isNaN(numValue)) return AlertType.DEFAULT;

      if (numValue >= 39) return AlertType.WARNING;
      if ((numValue >= 38.0 && numValue < 39) || numValue <= 35) return AlertType.ORANGE;
    }
    return AlertType.DEFAULT;
  },
  [obsMetricNames.consciousness]: (value: number | string | undefined): AlertType => {
    if (value !== undefined && value !== "") {
      if (value === "U") return AlertType.DANGER;
      if (value === "P") return AlertType.WARNING;
      if (value === "V" || value === "C") return AlertType.ORANGE;
    }
    return AlertType.DEFAULT;
  },
};

// Create default handlers for all other fields
const defaultFieldChecks = Object.fromEntries(
  Object.values(obsMetricNames)
    .filter(name => !(name in specificFieldChecks))
    .map(name => [name, () => AlertType.DEFAULT])
);

// Combine specific and default field checks
export const fieldChecks = {
  ...specificFieldChecks,
  ...defaultFieldChecks,
} as Record<ObsMetricAlias, (value: number | string | undefined, context?: ValidationContext) => AlertType>;

export const checkFields = (
  field: ObsMetricAlias,
  value: number | string | undefined,
  context?: ValidationContext
): AlertType => {
  const check = fieldChecks[field];
  if (check && value !== undefined) {
    return check(value, context);
  }
  return AlertType.DEFAULT;
};

